import { LocationProvider, Router, Route, useLocation } from "preact-iso";
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import {
  ArrowRightStartOnRectangleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { authClient, type SessionUser } from "./lib/auth-client";
import { api } from "./lib/api-client";
import { isPlatformAdminPath } from "./lib/platform-admin";
import { ToastProvider } from "./lib/use-toast";
import { Home } from "./routes/home";
import { ProjectDetail } from "./routes/project-detail";
import { ContentEdit } from "./routes/content-edit";
import { ContentChat } from "./routes/content-chat";
import { PageList } from "./routes/page-list";
import { ApiKeysPage } from "./routes/api-keys";
import { ProjectMembersPage } from "./routes/project-members";
import { AdminUsersPage } from "./routes/admin-users";
import { PlatformAdminHome } from "./routes/platform-admin-home";
import { Login } from "./routes/login";
import { NotFound } from "./routes/not-found";
import { HeaderModeToggle } from "./components/header-mode-toggle";
import { Button, IconButton } from "./components/ui";
import {
  NavigationGuardProvider,
  useNavigationGuard,
} from "./lib/navigation-guard";
import { useToast } from "./lib/use-toast";

function AppShell({ children }: { children: ComponentChildren }) {
  const { data: session, isPending } = authClient.useSession();
  const location = useLocation();
  const { confirmLeave } = useNavigationGuard();
  const toast = useToast();

  // Public routes that render without a session — anything else bounces to /login.
  // "/" is public because Home itself handles the logged-out redirect to the
  // standalone LP (see routes/home.tsx); it must not bounce to /login first.
  const isPublicPath = location.path === "/login" || location.path === "/";
  const mustSignIn = !isPending && !session && !isPublicPath;
  // Navigate from an effect, not during render: routing mid-render updates
  // the LocationProvider while this tree is still rendering.
  useEffect(() => {
    if (mustSignIn) location.route("/login", true);
  }, [mustSignIn]);
  if (mustSignIn) return null;

  const uiMode = (session?.user as SessionUser | undefined)?.uiMode ?? "simple";

  async function onSignOut() {
    if (!(await confirmLeave())) return;
    try {
      await authClient.signOut();
    } catch (e) {
      toast.error(`サインアウトに失敗しました: ${(e as Error).message}`);
    } finally {
      location.route("/login", true);
    }
  }

  return (
    <div class="flex min-h-screen flex-col bg-bg">
      <header class="sticky top-0 z-10 border-b border-border bg-bg">
        <div class="mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <a
            href="/"
            class="inline-flex items-center transition hover:opacity-70"
          >
            {/* h-4 below sm so logo + mode toggle + sign-out fit a 360px screen. */}
            <img src="/brand-text.svg" alt="Elwood CMS" class="h-4 w-auto sm:h-5" />
          </a>
          <nav class="flex items-center gap-2 sm:gap-3 text-xs text-fg-subtle">
            {session?.user && (
              <HeaderModeToggle uiMode={uiMode} currentPath={location.path} />
            )}
            {session?.user && (
              <>
                <span class="hidden font-mono text-fg-subtle md:inline">
                  {session.user.email}
                </span>
                {/* Wrapped: cn() doesn't dedupe, so `hidden` would race the
                    Button's own inline-flex. */}
                <span class="hidden sm:inline-flex">
                  <Button variant="ghost" size="sm" onClick={onSignOut}>
                    サインアウト
                  </Button>
                </span>
                <IconButton
                  ariaLabel="サインアウト"
                  class="sm:hidden"
                  onClick={onSignOut}
                >
                  <ArrowRightStartOnRectangleIcon class="h-4 w-4" />
                </IconButton>
              </>
            )}
            {!isPending && !session && location.path !== "/login" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => location.route("/login")}
              >
                サインイン
              </Button>
            )}
          </nav>
        </div>
      </header>
      <main class="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-10">
        {children}
      </main>
    </div>
  );
}

function PlatformAdminShell({ children }: { children: ComponentChildren }) {
  const location = useLocation();
  const [identity, setIdentity] = useState<{ email: string | null } | null>(null);

  function onAccessLogout(e: MouseEvent) {
    // preact-iso intercepts same-origin anchors at the window level. Bypass its
    // SPA navigation so Cloudflare receives the request and clears the Access
    // authorization cookie.
    e.preventDefault();
    e.stopPropagation();
    window.location.assign("/cdn-cgi/access/logout");
  }

  useEffect(() => {
    let alive = true;
    void api
      .getPlatformSession()
      .then((result) => {
        if (alive) setIdentity(result.identity);
      })
      // request() already starts the Access re-authentication reload on 401.
      // Swallow the rejected promise here so that transition is not reported
      // as an unrelated unhandled error in the console.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div class="flex min-h-screen flex-col bg-bg">
      <header class="sticky top-0 z-10 border-b border-border bg-bg">
        <div class="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
          <a
            href="/platform-admin"
            class="inline-flex items-center gap-2 text-sm font-semibold text-fg transition hover:opacity-70"
          >
            <ShieldCheckIcon class="h-5 w-5 text-accent" />
            <span>Elwood Platform</span>
          </a>
          <nav class="flex items-center gap-2 text-xs text-fg-subtle">
            <Button
              as="a"
              href="/platform-admin"
              variant={location.path === "/platform-admin" ? "secondary" : "ghost"}
              size="sm"
            >
              プロジェクト
            </Button>
            <Button
              as="a"
              href="/platform-admin/users"
              variant={location.path === "/platform-admin/users" ? "secondary" : "ghost"}
              size="sm"
            >
              ユーザー
            </Button>
            <span class="hidden font-mono text-fg-subtle md:inline">
              {identity?.email ?? "Cloudflare Access"}
            </span>
            <Button
              as="a"
              href="/cdn-cgi/access/logout"
              onClick={onAccessLogout}
              variant="ghost"
              size="sm"
            >
              Accessログアウト
            </Button>
          </nav>
        </div>
      </header>
      <main class="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 sm:py-10">
        {children}
      </main>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (isPlatformAdminPath(location.path)) {
    return (
      <PlatformAdminShell>
        <Router>
          <Route path="/platform-admin" component={PlatformAdminHome} />
          <Route path="/platform-admin/users" component={AdminUsersPage} />
          <Route
            path="/platform-admin/projects/:projectId"
            component={ProjectDetail}
          />
          <Route
            path="/platform-admin/projects/:projectId/keys"
            component={ApiKeysPage}
          />
          <Route
            path="/platform-admin/projects/:projectId/members"
            component={ProjectMembersPage}
          />
          <Route
            path="/platform-admin/projects/:projectId/contents/:slug"
            component={ContentEdit}
          />
          <Route
            path="/platform-admin/projects/:projectId/pages"
            component={PageList}
          />
          <Route
            path="/platform-admin/projects/:projectId/pages/:slug"
            component={ContentChat}
          />
          <Route default component={NotFound} />
        </Router>
      </PlatformAdminShell>
    );
  }

  return (
    <AppShell>
      <Router>
        <Route path="/login" component={Login} />
        <Route path="/" component={Home} />
        <Route path="/projects/:projectId" component={ProjectDetail} />
        <Route path="/projects/:projectId/keys" component={ApiKeysPage} />
        <Route path="/projects/:projectId/members" component={ProjectMembersPage} />
        <Route path="/projects/:projectId/contents/:slug" component={ContentEdit} />
        <Route path="/projects/:projectId/pages" component={PageList} />
        <Route path="/projects/:projectId/pages/:slug" component={ContentChat} />
        <Route default component={NotFound} />
      </Router>
    </AppShell>
  );
}

export function App() {
  return (
    <LocationProvider>
      {/* Above Router so toasts survive route transitions. */}
      <ToastProvider>
        <NavigationGuardProvider>
          <AppRoutes />
        </NavigationGuardProvider>
      </ToastProvider>
    </LocationProvider>
  );
}
